/**
 * Company Information Step
 *
 * Second step - collect comprehensive company details including
 * logo, address, contact information, and tax details
 */

import { useState, useEffect, useRef, useMemo, type ChangeEvent } from 'react'

import { SaveStatusIndicator, LogoUploadSection } from './CompanyInfoStepComponents'
import {
  formatDateToString,
  validatePANInput,
  validateGSTINInput,
  handleBusinessIdPaste,
  cleanEmailInput,
  handleEmailPaste,
  validateCompanyInfo,
  focusFirstError,
} from './CompanyInfoStepHelpers'
import {
  BasicInfoSection,
  AddressSection,
  ContactInfoSection,
  TaxInfoSection,
  CompanyInfoActions,
  COUNTRY_LABELS,
} from './CompanyInfoStepSections'
import {
  getCountries,
  getStates,
  getCountryTimezones,
  getCountryCurrency,
  getCountryByCode,
  type Country,
  type State,
  type Timezone,
} from '../../services/geolocation-service'

import type { CompanySetupData } from '../../types/company-setup'

interface CompanyInfoStepProps {
  companyInfo: CompanySetupData['companyInfo']
  onChange: (info: CompanySetupData['companyInfo']) => void
  onNext: () => void
  onBack: () => void
}

interface CompanyInfoState {
  localInfo: LocalInfo
  setLocalInfo: React.Dispatch<React.SetStateAction<LocalInfo>>
  errors: Record<string, string>
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
  states: State[]
  setStates: React.Dispatch<React.SetStateAction<State[]>>
  timezones: Timezone[]
  setTimezones: React.Dispatch<React.SetStateAction<Timezone[]>>
  selectedCountry: Country | null
  setSelectedCountry: React.Dispatch<React.SetStateAction<Country | null>>
  isLoadingTimezones: boolean
  setIsLoadingTimezones: React.Dispatch<React.SetStateAction<boolean>>
  logoPreview: string | null
  setLogoPreview: React.Dispatch<React.SetStateAction<string | null>>
  saveStatus: 'idle' | 'saving' | 'saved'
  setSaveStatus: React.Dispatch<React.SetStateAction<'idle' | 'saving' | 'saved'>>
  saveTimerRef: React.MutableRefObject<NodeJS.Timeout | null>
}

function useCompanyInfoState(companyInfo: LocalInfo): CompanyInfoState {
  const [localInfo, setLocalInfo] = useState(companyInfo)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [states, setStates] = useState<State[]>([])
  const [timezones, setTimezones] = useState<Timezone[]>([])
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(true)
  const [logoPreview, setLogoPreview] = useState<string | null>(localInfo.logo ?? null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const saveTimerRef = useRef<NodeJS.Timeout | null>(null)
  return { localInfo, setLocalInfo, errors, setErrors, states, setStates, timezones, setTimezones, selectedCountry, setSelectedCountry, isLoadingTimezones, setIsLoadingTimezones, logoPreview, setLogoPreview, saveStatus, setSaveStatus, saveTimerRef }
}

function useCompanyInfoEffects(state: CompanyInfoState, onChange: (info: LocalInfo) => void): void {
  const { localInfo, setLocalInfo, setStates, setTimezones, selectedCountry, setSelectedCountry, setIsLoadingTimezones, setSaveStatus, saveTimerRef, timezones } = state
  useEffect(() => {
    void loadCountries(() => {}, () => {})
    const countryCode = localInfo.country ?? 'IN'
    void loadStates(countryCode, setStates, () => {})
    void loadTimezones(countryCode, setTimezones, setIsLoadingTimezones)
    void loadCountryData(countryCode, setSelectedCountry)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, [])
  useEffect(() => {
    if (localInfo.country) { void loadStates(localInfo.country, setStates, () => {}) }
  }, [localInfo.country, setStates])
  useEffect(() => {
    if (localInfo.country) { void loadTimezones(localInfo.country, setTimezones, setIsLoadingTimezones) }
  }, [localInfo.country, setTimezones, setIsLoadingTimezones])
  useEffect(() => {
    const firstTimezone = timezones[0]
    if (timezones.length > 0 && !localInfo.timezone && firstTimezone) {
      setLocalInfo((prev) => ({ ...prev, timezone: firstTimezone.identifier }))
    }
  }, [timezones, localInfo.timezone, setLocalInfo])
  useEffect(() => {
    const countryCode = localInfo.country ?? 'IN'
    void loadCountryData(countryCode, setSelectedCountry)
  }, [localInfo.country, setSelectedCountry])
  // buildUpdatedInfo sets a provisional currency from the crude 12-country
  // CURRENCY_MAP so the field updates instantly; once the full ~195-country
  // geolocation record for the selected country loads, correct it to the
  // authoritative currencyCode (e.g. a country outside that map no longer
  // gets stuck on the map's INR/USD fallback).
  useEffect(() => {
    const updater = correctedCurrencyUpdater(selectedCountry, localInfo.country, localInfo.currency)
    if (updater) { setLocalInfo(updater) }
  }, [selectedCountry, localInfo.country, localInfo.currency, setLocalInfo])
  useEffect(() => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current) }
    setSaveStatus('saving')
    saveTimerRef.current = setTimeout(() => {
      onChange(localInfo)
      setSaveStatus('saved')
      setTimeout(() => { setSaveStatus('idle') }, 2000)
    }, 1500)
    return (): void => { if (saveTimerRef.current) { clearTimeout(saveTimerRef.current) } }
  }, [localInfo, onChange, saveTimerRef, setSaveStatus])
}

interface CompanyInfoHandlers {
  handleChange: (field: keyof LocalInfo, value: string) => void
  handleLogoUpload: (e: ChangeEvent<HTMLInputElement>) => void
  handleRemoveLogo: () => void
  handlePANChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleGSTINChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleBusinessIdPasteEvent: (e: React.ClipboardEvent<HTMLInputElement>, type: 'pan' | 'gstin') => void
  handleEmailChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleEmailPasteEvent: (e: React.ClipboardEvent<HTMLInputElement>) => void
}

function buildLogoHandlers(state: CompanyInfoState): Pick<CompanyInfoHandlers, 'handleLogoUpload' | 'handleRemoveLogo'> {
  const { setErrors, setLogoPreview, setLocalInfo } = state
  return {
    handleLogoUpload: (e) => {
      const file = e.target.files?.[0]
      if (!file) { return }
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, logo: 'Please select an image file' }))
        return
      }
      if (file.size > 2 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, logo: 'Image size must be less than 2MB' }))
        return
      }
      const reader = new FileReader()
      reader.onloadend = (): void => {
        const base64 = reader.result as string
        setLogoPreview(base64)
        setLocalInfo((prev) => ({ ...prev, logo: base64 }))
        setErrors((prev) => ({ ...prev, logo: '' }))
      }
      reader.readAsDataURL(file)
    },
    handleRemoveLogo: () => {
      setLogoPreview(null)
      setLocalInfo((prev) => ({ ...prev, logo: undefined }))
    },
  }
}

function buildCompanyInfoHandlers(state: CompanyInfoState): CompanyInfoHandlers {
  const { setLocalInfo, setErrors } = state
  const handleChange = (field: keyof LocalInfo, value: string): void => {
    setLocalInfo((prev) => buildUpdatedInfo(prev, field, value))
    setErrors((prev) => ({ ...prev, [field]: '' }))
  }
  return {
    handleChange,
    ...buildLogoHandlers(state),
    handlePANChange: (e) => handleChange('pan', validatePANInput(e.target.value)),
    handleGSTINChange: (e) => {
      const result = validateGSTINInput(e.target.value)
      if (result.extractedPAN) {
        setLocalInfo((prev) => ({ ...prev, gstin: result.gstin, pan: result.extractedPAN }))
        setErrors((prev) => ({ ...prev, gstin: '', pan: '' }))
      } else {
        handleChange('gstin', result.gstin)
      }
    },
    handleBusinessIdPasteEvent: (e, type) => {
      const result = handleBusinessIdPaste(e, type)
      if (result.gstin !== undefined && result.pan !== undefined) {
        setLocalInfo((prev) => ({ ...prev, gstin: result.gstin ?? '', pan: result.pan ?? '' }))
        setErrors((prev) => ({ ...prev, gstin: '', pan: '' }))
      } else if (result.pan !== undefined) {
        handleChange('pan', result.pan)
      } else if (result.gstin !== undefined) {
        handleChange('gstin', result.gstin)
      }
    },
    handleEmailChange: (e) => handleChange('email', cleanEmailInput(e.target.value)),
    handleEmailPasteEvent: (e) => handleChange('email', handleEmailPaste(e)),
  }
}

interface SectionsProps {
  state: CompanyInfoState
  handlers: CompanyInfoHandlers
  stateFieldLabel: string
  postalCodeLabel: string
  postalCodeExample: string
  postalCodeInputMode: 'numeric' | 'text'
}

function CompanyInfoSections({ state, handlers, stateFieldLabel, postalCodeLabel, postalCodeExample, postalCodeInputMode }: SectionsProps): JSX.Element {
  return (
    <>
      <LogoUploadSection
        error={state.errors.logo}
        logoPreview={state.logoPreview}
        onRemove={handlers.handleRemoveLogo}
        onUpload={handlers.handleLogoUpload}
      />
      <BasicInfoSection
        errors={state.errors}
        isLoadingTimezones={state.isLoadingTimezones}
        localInfo={state.localInfo}
        timezones={state.timezones}
        onChange={handlers.handleChange}
      />
      <AddressSection
        localInfo={state.localInfo}
        postalCodeExample={postalCodeExample}
        postalCodeInputMode={postalCodeInputMode}
        postalCodeLabel={postalCodeLabel}
        stateFieldLabel={stateFieldLabel}
        states={state.states}
        onChange={handlers.handleChange}
      />
      <ContactInfoSection
        errors={state.errors}
        localInfo={state.localInfo}
        onChange={handlers.handleChange}
        onEmailChange={handlers.handleEmailChange}
        onEmailPaste={handlers.handleEmailPasteEvent}
        onPhoneChange={(value) => state.setLocalInfo((prev) => ({ ...prev, phone: value }))}
      />
      <TaxInfoSection
        errors={state.errors}
        localInfo={state.localInfo}
        onGSTINChange={handlers.handleGSTINChange}
        onGSTINPaste={(e) => handlers.handleBusinessIdPasteEvent(e, 'gstin')}
        onPANChange={handlers.handlePANChange}
        onPANPaste={(e) => handlers.handleBusinessIdPasteEvent(e, 'pan')}
      />
    </>
  )
}

export function CompanyInfoStep({
  companyInfo,
  onChange,
  onNext,
  onBack,
}: CompanyInfoStepProps): JSX.Element {
  const state = useCompanyInfoState(companyInfo)
  useCompanyInfoEffects(state, onChange)
  const handlers = buildCompanyInfoHandlers(state)

  const stateFieldLabel = useMemo(() => deriveStateFieldLabel(state.localInfo.country, state.states), [state.states, state.localInfo.country])
  const postalCodeLabel = useMemo(() => state.selectedCountry?.postalCodeName ?? 'Postal Code', [state.selectedCountry])
  const postalCodeExample = useMemo(() => state.selectedCountry?.postalCodeExample ?? '12345', [state.selectedCountry])
  const postalCodeInputMode = useMemo((): 'numeric' | 'text' => {
    const format = state.selectedCountry?.postalCodeFormat
    if (!format) { return 'numeric' }
    return /A/.test(format) ? 'text' : 'numeric'
  }, [state.selectedCountry])

  const handleNext = (): void => {
    const newErrors = validateCompanyInfo(state.localInfo)
    state.setErrors(newErrors as Record<string, string>)
    focusFirstError(newErrors)
    if (Object.keys(newErrors).length !== 0) { return }
    if (state.saveTimerRef.current) { clearTimeout(state.saveTimerRef.current); state.saveTimerRef.current = null }
    onChange(state.localInfo)
    setTimeout(() => { onNext() }, 100)
  }

  return (
    <div className="space-y-8">
      <SaveStatusIndicator status={state.saveStatus} />
      <CompanyInfoSections
        handlers={handlers}
        postalCodeExample={postalCodeExample}
        postalCodeInputMode={postalCodeInputMode}
        postalCodeLabel={postalCodeLabel}
        state={state}
        stateFieldLabel={stateFieldLabel}
      />
      <CompanyInfoActions onBack={onBack} onNext={handleNext} />
    </div>
  )
}

// =============================================================================
// Pure helpers (no hooks) — extracted to keep the component function short
// =============================================================================

function deriveStateFieldLabel(country: string | undefined, states: { administrativeType?: string }[]): string {
  if (country && COUNTRY_LABELS[country]) { return COUNTRY_LABELS[country] }
  if (states.length > 0) {
    const typeCounts = new Map<string, number>()
    states.forEach(state => {
      if (state.administrativeType) {
        typeCounts.set(state.administrativeType, (typeCounts.get(state.administrativeType) ?? 0) + 1)
      }
    })
    if (typeCounts.size > 0) {
      const sorted = Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1])
      return sorted[0]?.[0] ?? 'State / Province'
    }
  }
  return 'State / Province'
}

type LocalInfo = CompanySetupData['companyInfo']

// Corrects the provisional currency (set by buildUpdatedInfo from the crude
// 12-country CURRENCY_MAP) once the full ~195-country geolocation record for
// the selected country has loaded, so any country outside that map doesn't
// stay stuck on the map's fallback. Returns null when no correction applies.
function correctedCurrencyUpdater(
  selectedCountry: Country | null,
  currentCountry: string | undefined,
  currentCurrency: string | undefined
): ((prev: LocalInfo) => LocalInfo) | null {
  if (!selectedCountry || selectedCountry.identifier !== currentCountry || !selectedCountry.currencyCode) {
    return null
  }
  if (selectedCountry.currencyCode === currentCurrency) {
    return null
  }
  const countryId = selectedCountry.identifier
  const currencyCode = selectedCountry.currencyCode
  return (prev) => (
    prev.country === countryId && prev.currency !== currencyCode
      ? { ...prev, currency: currencyCode }
      : prev
  )
}

function buildUpdatedInfo(prev: LocalInfo, field: keyof LocalInfo, value: string): LocalInfo {
  const updated = { ...prev, [field]: value }
  if (field === 'country' && value) {
    updated.currency = getCountryCurrency(value)
  }
  if (field === 'country' && value !== prev.country) {
    updated.state = ''
    updated.timezone = ''
  }
  if (field === 'fiscalYearStart' && value) {
    const startDate = new Date(value)
    const endDate = new Date(startDate)
    endDate.setFullYear(endDate.getFullYear() + 1)
    endDate.setDate(endDate.getDate() - 1)
    updated.fiscalYearEnd = formatDateToString(endDate)
  }
  if (field === 'fiscalYearEnd' && value) {
    const endDate = new Date(value)
    const startDate = new Date(endDate)
    startDate.setFullYear(startDate.getFullYear() - 1)
    startDate.setDate(startDate.getDate() + 1)
    updated.fiscalYearStart = formatDateToString(startDate)
  }
  return updated
}

async function loadCountries(
  setCountries: (c: Country[]) => void,
  setLoading: (v: boolean) => void
): Promise<void> {
  setLoading(true)
  try { setCountries(await getCountries()) } catch { /* silently ignore */ } finally { setLoading(false) }
}

async function loadStates(
  countryCode: string,
  setStates: (s: State[]) => void,
  setLoading: (v: boolean) => void
): Promise<void> {
  setLoading(true)
  try { setStates(await getStates(countryCode)) } catch { /* silently ignore */ } finally { setLoading(false) }
}

async function loadTimezones(
  countryCode: string,
  setTimezones: (t: Timezone[]) => void,
  setLoading: (v: boolean) => void
): Promise<void> {
  setLoading(true)
  try { setTimezones(await getCountryTimezones(countryCode)) } catch { /* silently ignore */ } finally { setLoading(false) }
}

async function loadCountryData(
  countryCode: string,
  setSelectedCountry: (c: Country | null) => void
): Promise<void> {
  try { setSelectedCountry((await getCountryByCode(countryCode)) ?? null) } catch { /* silently ignore */ }
}
